// src/pages/qbo/CleanupCard.tsx
//
// Desktop-migration cleanup (agent 3.18.14). The plan lists, per customer,
// the open QuickBooks items dated on/before the cutoff that Advantage has
// settled or does not know, plus stray journal / deposit / check lines.
// Posting clears them: one journal per customer moving the stale total to
// the placeholder, then a zero-dollar payment marking the items paid.
// Nothing posts until "Post cleanup" is confirmed.
//
// Scenarios (2026-10-23): every item is bucketed so the definite ones can be
// cleared first and the bookkeeper reviews only the rest. A dead document is
// definite (Advantage settled it, or never knew it), and so is a stray line
// from the Desktop era (before the daily summary journals began). A hand
// journal entry / check / deposit posted on a named customer INSIDE the
// daily-journal era is "review": it may duplicate a daily journal (bounced
// ACH re-journaled, adjustments made only in QuickBooks …) or be a genuine
// open item. The era start is editable (Devash: June 2022).

import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Eraser } from 'lucide-react';
import qboApi from '../../services/qboApi';
import SyncResultList from './SyncResultList';
import type { CleanupPlan, CleanupUndoRow, SyncResult } from './qboTypes';
import { money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
}

type PlanRow = CleanupPlan['rows'][number];
type PlanItem = PlanRow['items'][number];

type Scenario = 'paid' | 'unknown' | 'oldline' | 'matched' | 'je' | 'line';
const SCENARIOS: { key: Scenario; label: string; short: string; definite: boolean; hint: string }[] = [
  { key: 'paid', label: 'Dead document, paid in Advantage', short: 'paid', definite: true, hint: 'Invoice / credit still open in QuickBooks that Advantage settled years ago.' },
  { key: 'unknown', label: 'Dead document, unknown to Advantage', short: 'unknown', definite: true, hint: 'Invoice number Advantage does not have (Desktop-only numbering).' },
  { key: 'oldline', label: 'Stray line, Desktop era', short: 'old line', definite: true, hint: 'Journal / check / deposit line dated before the daily summary journals began: a frozen Desktop-era lump.' },
  { key: 'matched', label: 'Hand entry matched to an Advantage receipt', short: 'matched', definite: true, hint: 'Journal / check / deposit on a named customer whose amount matches an Advantage receipt within a week: the daily cash journal already carried that money, so the hand entry duplicates it (agent 3.19.5).' },
  { key: 'je', label: 'Hand journal entry, daily-journal era', short: 'JE', definite: false, hint: 'Journal entry posted by hand on a named customer while all sales and receipts went through the placeholder. May duplicate a daily journal (bounced ACH, re-journaled receipt, adjustment) or be genuinely open — the bookkeeper decides.' },
  { key: 'line', label: 'Check / deposit, daily-journal era', short: 'check/dep', definite: false, hint: 'Check or deposit recorded against a named customer inside the daily-journal era. Same question as the hand journal entries.' },
];
const SCEN = Object.fromEntries(SCENARIOS.map((s) => [s.key, s])) as Record<Scenario, (typeof SCENARIOS)[number]>;

const isDoc = (i: PlanItem) => /invoice|credit/i.test(i.type) || /in advantage/i.test(i.why);
const scenarioOf = (i: PlanItem, eraStart: string): Scenario => {
  if (isDoc(i)) return /not in advantage/i.test(i.why) ? 'unknown' : 'paid';
  if (i.date && i.date < eraStart) return 'oldline';
  if (i.matched) return 'matched';
  return /journal/i.test(i.type) ? 'je' : 'line';
};

type Filter = 'all' | 'definite' | 'review' | Scenario;

export default function CleanupCard({ onMessage }: Props) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<CleanupPlan | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);
  // 3.23.2: customers already cleared whose MIGR journal is still open next to the items; undo of a duplicate run
  const [undoSel, setUndoSel] = useState<Set<string>>(new Set());
  const [undoRows, setUndoRows] = useState<CleanupUndoRow[] | null>(null);
  const [undoing, setUndoing] = useState(false);
  // customers with an open MIGR journal: fully covered ones (plan.covered) plus rows the journal over- or under-covers
  const undoCandidates = useMemo(() => {
    if (!plan) return [] as { qboId: string; name: string; items: number; journalOpen: number; itemCount: number; remaining: number }[];
    const covered = (plan.covered || []).map((c) => ({ ...c, remaining: 0 }));
    const rows = plan.rows.filter((r) => (r.journalOpen ?? 0) !== 0).map((r) => ({ qboId: r.qboId, name: r.name, items: r.items.reduce((a, i) => a + i.open, 0), journalOpen: r.journalOpen ?? 0, itemCount: r.items.length, remaining: r.clear }));
    return [...covered, ...rows];
  }, [plan]);
  const undoPlan = async () => {
    try { setUndoRows((await qboApi.conversionCleanupUndoPlan([...undoSel])) as CleanupUndoRow[]); }
    catch (e: unknown) { onMessage('error', (e as Error).message); }
  };
  const undoRun = async () => {
    if (!undoRows) return;
    setUndoing(true);
    try {
      const r = (await qboApi.conversionCleanupUndo(undoRows.filter((x) => !x.error).map((x) => x.qboId))) as SyncResult;
      setResult(r);
      onMessage(r.errors ? 'error' : 'success', `Undo: ${r.updated} journal(s) deleted, ${r.skipped} skipped, ${r.errors} errors`);
      setUndoRows(null); setUndoSel(new Set());
      await load();
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setUndoing(false); }
  };
  const [eraStart, setEraStart] = useState('2022-06-01');
  const [postDate, setPostDate] = useState('');   // posting date of the MIGR journals + payments; '' = today (agent default)
  const [filter, setFilter] = useState<Filter>('all');

  const load = async () => {
    setLoading(true);
    setResult(null);
    setConfirming(false);
    try {
      const p = (await qboApi.conversionCleanupPlan()) as CleanupPlan;
      setPlan(p);
      setPostDate((d) => d || p.cutoff);   // default: the cutoff, so an aging as of the cutoff is clean
      setSelected(new Set());
      setExpanded(new Set());
    } catch (e: unknown) {
      onMessage('error', `Cleanup plan: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Per-row scenario breakdown and per-scenario totals.
  const classified = useMemo(() => {
    const rows = (plan?.rows ?? []).map((r) => {
      const counts: Partial<Record<Scenario, { n: number; open: number }>> = {};
      const items = r.items.map((i) => {
        const s = scenarioOf(i, eraStart);
        const c = (counts[s] ??= { n: 0, open: 0 });
        c.n++; c.open += i.open;
        return { ...i, scenario: s };
      });
      const definite = Object.keys(counts).every((k) => SCEN[k as Scenario].definite);
      return { row: r, items, counts, definite };
    });
    const totals: Record<Scenario, { items: number; open: number; customers: number }> =
      { paid: { items: 0, open: 0, customers: 0 }, unknown: { items: 0, open: 0, customers: 0 }, oldline: { items: 0, open: 0, customers: 0 }, matched: { items: 0, open: 0, customers: 0 }, je: { items: 0, open: 0, customers: 0 }, line: { items: 0, open: 0, customers: 0 } };
    for (const c of rows) for (const [k, v] of Object.entries(c.counts)) { const t = totals[k as Scenario]; t.items += v!.n; t.open += v!.open; t.customers++; }
    const definiteRows = rows.filter((c) => c.definite);
    const reviewRows = rows.filter((c) => !c.definite);
    return { rows, totals, definiteRows, reviewRows };
  }, [plan, eraStart]);

  const shown = useMemo(() => {
    if (filter === 'all') return classified.rows;
    if (filter === 'definite') return classified.definiteRows;
    if (filter === 'review') return classified.reviewRows;
    return classified.rows.filter((c) => c.counts[filter]);
  }, [classified, filter]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleExpand = (id: string) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectable = plan?.rows ?? [];
  // 3.19.5: by default the run leaves unconfirmed review items (unmatched hand entries) on the customer.
  const [includeReview, setIncludeReview] = useState(false);
  const excludedIds = useMemo(() => {
    if (includeReview) return [] as string[];
    return classified.rows.filter((c) => selected.has(c.row.qboId)).flatMap((c) => c.items.filter((i) => !SCEN[i.scenario].definite).map((i) => i.id));
  }, [classified, selected, includeReview]);
  const selectedClearNow = useMemo(() => {
    const ex = new Set(excludedIds);
    return classified.rows.filter((c) => selected.has(c.row.qboId)).reduce((a, c) => a + c.items.filter((i) => !ex.has(i.id)).reduce((b, i) => b + i.open, 0), 0);
  }, [classified, selected, excludedIds]);
  const shownSelectable = shown.filter((c) => !c.row.alreadyCleared).map((c) => c.row.qboId);
  const sum = (rows: typeof classified.rows) => rows.reduce((a, c) => a + c.row.clear, 0);

  const downloadCsv = () => {
    if (!plan) return;
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [['Customer', 'QBO id', 'Scenario', 'Definite', 'Type', 'Date', 'Number', 'Open', 'Why', 'Customer balance now', 'Customer moves', 'Customer stays', 'Already cleared'].map(esc).join(',')];
    for (const c of classified.rows)
      for (const i of c.items)
        lines.push([c.row.name, c.row.qboId, SCEN[i.scenario].label, SCEN[i.scenario].definite ? 'yes' : 'REVIEW', i.type, i.date, i.num, i.open.toFixed(2), i.why, c.row.balance.toFixed(2), c.row.clear.toFixed(2), c.row.afterBalance.toFixed(2), c.row.alreadyCleared ? 'yes' : ''].map(esc).join(','));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `qbo-cleanup-plan-${plan.cutoff}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const run = async () => {
    if (!plan || selected.size === 0) return;
    if (!confirming) { setConfirming(true); return; }
    setConfirming(false);
    setRunning(true);
    setResult(null);
    try {
      const all = selected.size === selectable.length;
      const body = { ...(all ? { all: true } : { qboIds: [...selected] }), ...(postDate ? { date: postDate } : {}), ...(excludedIds.length ? { excludeIds: excludedIds } : {}) };
      const res = (await runJob(() => qboApi.conversionCleanupRun(body), setJob)) as SyncResult;
      setResult(res);
      onMessage(res.errors ? 'error' : 'success', `Cleanup: ${res.created} customer(s) cleared, ${res.errors} error(s)`);
      await load();
    } catch (e: unknown) {
      onMessage('error', `Cleanup: ${(e as Error).message}`);
    } finally {
      setRunning(false);
      setJob(null);
    }
  };

  const chip = (key: Filter, label: string, sub: string, tone: string) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      className={`text-left rounded-lg p-2.5 border transition-colors ${filter === key ? 'border-blue-400 bg-slate-900' : 'border-slate-700/60 bg-slate-900/60 hover:border-slate-500'}`}
    >
      <div className={`text-[10px] uppercase ${tone}`}>{label}</div>
      <div className="text-xs font-mono text-slate-200">{sub}</div>
    </button>
  );

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-400 flex items-center gap-2"><Eraser size={14} /> Desktop-migration cleanup</div>
          <div className="text-[11px] text-slate-500">Moves the dead receivables (items dated on/before the cutoff that Advantage has settled or does not know, plus stray journal lines) from the customers to the placeholder and marks them paid. One journal and one zero-dollar payment per customer; no income account is touched.</div>
        </div>
        <button className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-slate-600 hover:bg-slate-500 disabled:opacity-50" onClick={load} disabled={loading || running}>
          {loading ? 'Loading…' : plan ? 'Reload plan' : 'Preview cleanup'}
        </button>
      </div>

      {plan && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Customers to clear</div><div className="text-sm font-mono text-white">{plan.customers}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Dead documents</div><div className="text-sm font-mono text-amber-300">${money(plan.staleDocumentsTotal)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Stray lines</div><div className="text-sm font-mono text-amber-300">${money(plan.staleLumpsTotal)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Total to move</div><div className="text-sm font-mono text-white">${money(plan.clearTotal)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Placeholder</div><div className="text-sm font-mono text-white">${money(plan.placeholderBalance)} → ${money(plan.expectedPlaceholderAfter)}</div><div className="text-[11px] text-slate-500">after a full cleanup</div></div>
          </div>

          <ul className="text-[11px] text-slate-500 space-y-0.5">{plan.notes.map((n, i) => <li key={i}>• {n}</li>)}</ul>

          {/* Scenarios */}
          <div className="rounded-lg border border-slate-700/60 p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="text-slate-300 font-medium">Scenarios</span>
              <span className="text-slate-500">Definite = Advantage settled or never knew the document, or the line predates the daily journals. Review = posted by hand on a named customer inside the daily-journal era.</span>
              <label className="ml-auto flex items-center gap-1 text-slate-400">Daily journals since
                <input type="date" value={eraStart} onChange={(e) => setEraStart(e.target.value || '2022-06-01')} className="bg-slate-900 border border-slate-600 rounded px-1.5 py-0.5 text-xs text-slate-200" />
              </label>
              <button className="flex items-center gap-1 text-blue-300 hover:underline" onClick={downloadCsv} title="Every item with its scenario, for the bookkeeper"><Download size={12} /> CSV for review</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              {chip('all', 'All customers', `${classified.rows.length} · $${money(sum(classified.rows))}`, 'text-slate-400')}
              {chip('definite', 'Definite only', `${classified.definiteRows.length} · $${money(sum(classified.definiteRows))}`, 'text-green-400')}
              {chip('review', 'Needs review', `${classified.reviewRows.length} · $${money(sum(classified.reviewRows))}`, 'text-amber-400')}
              {SCENARIOS.map((s) => chip(s.key, s.label, `${classified.totals[s.key].customers} cust · ${classified.totals[s.key].items} items · $${money(classified.totals[s.key].open)}`, s.definite ? 'text-green-400/80' : 'text-amber-400/80'))}
            </div>
            {filter !== 'all' && filter !== 'definite' && filter !== 'review' && <div className="text-[11px] text-slate-500">{SCEN[filter].hint}</div>}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button className="text-blue-300 hover:underline" onClick={() => setSelected((s) => new Set([...s, ...shownSelectable]))}>select all shown ({shownSelectable.length})</button>
            <button className="text-blue-300 hover:underline" onClick={() => setSelected(new Set())}>clear</button>
            <button className="text-blue-300 hover:underline" onClick={() => setExpanded(expanded.size ? new Set() : new Set(shown.map((c) => c.row.qboId)))}>{expanded.size ? 'collapse all' : 'expand all shown'}</button>
            <span className="text-slate-500">{selected.size} customer(s) selected · <span className="font-mono">${money(selectedClearNow)}</span>{excludedIds.length > 0 && <span className="text-amber-300"> · {excludedIds.length} review item(s) left on the customers</span>}</span>
          </div>

          <div className="max-h-[520px] overflow-y-auto rounded border border-slate-700/60">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-slate-800">
                <tr className="text-[10px] uppercase text-slate-500">
                  <th className="px-2 py-1.5 border-b border-slate-700 w-6" />
                  <th className="px-2 py-1.5 border-b border-slate-700 w-5" />
                  <th className="text-left px-2 py-1.5 border-b border-slate-700">Customer</th>
                  <th className="text-left px-2 py-1.5 border-b border-slate-700">Scenarios</th>
                  <th className="text-right px-2 py-1.5 border-b border-slate-700">Balance now</th>
                  <th className="text-right px-2 py-1.5 border-b border-slate-700">Dead docs</th>
                  <th className="text-right px-2 py-1.5 border-b border-slate-700">Stray lines</th>
                  <th className="text-right px-2 py-1.5 border-b border-slate-700">Moves</th>
                  <th className="text-right px-2 py-1.5 border-b border-slate-700">Stays</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ row: r, items, counts, definite }) => (
                  <Fragment key={r.qboId}>
                    <tr className="hover:bg-slate-700/30">
                      <td className="px-2 py-1 border-b border-slate-800/40"><input type="checkbox" checked={selected.has(r.qboId)} onChange={() => toggle(r.qboId)} disabled={running} /></td>
                      <td className="px-1 py-1 border-b border-slate-800/40 text-slate-500 cursor-pointer" onClick={() => toggleExpand(r.qboId)}>{expanded.has(r.qboId) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-slate-300 cursor-pointer" onClick={() => toggleExpand(r.qboId)}>{r.name} <span className="text-slate-600">QBO {r.qboId}</span>{r.alreadyCleared && <span className="ml-1 text-green-400" title="an earlier run cleared part of this customer; these items remain">partly cleared</span>}</td>
                      <td className="px-2 py-1 border-b border-slate-800/40">
                        <span className={`mr-1.5 text-[10px] uppercase ${definite ? 'text-green-400' : 'text-amber-400'}`}>{definite ? 'definite' : 'review'}</span>
                        {SCENARIOS.filter((s) => counts[s.key]).map((s) => (
                          <span key={s.key} className={`inline-block mr-1 px-1 rounded text-[10px] ${s.definite ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/40 text-amber-300'}`} title={s.label}>{s.short} {counts[s.key]!.n}</span>
                        ))}
                      </td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-right font-mono text-slate-400">{money(r.balance)}</td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-right font-mono text-amber-300">{money(r.staleDocumentsOpen)} <span className="text-slate-600">({r.staleDocuments})</span></td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-right font-mono text-amber-300">{money(r.staleLumpsOpen)} <span className="text-slate-600">({r.staleLumps})</span></td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-right font-mono text-white">{money(r.clear)}</td>
                      <td className="px-2 py-1 border-b border-slate-800/40 text-right font-mono text-green-400">{money(r.afterBalance)}</td>
                    </tr>
                    {expanded.has(r.qboId) && (
                      <tr>
                        <td colSpan={9} className="px-2 py-1 border-b border-slate-800/40 bg-slate-900/50">
                          <table className="text-[11px] w-full">
                            <tbody>
                              {items.map((i) => (
                                <tr key={i.id} className={SCEN[i.scenario].definite ? 'text-slate-400' : 'text-amber-200'}>
                                  <td className="pr-3 py-0.5 font-mono whitespace-nowrap">{i.date}</td>
                                  <td className="pr-3 py-0.5 whitespace-nowrap">{i.type} {i.num}</td>
                                  <td className="pr-3 py-0.5 font-mono text-right whitespace-nowrap">{money(i.open)}</td>
                                  <td className="pr-3 py-0.5"><span className={`px-1 rounded text-[10px] ${SCEN[i.scenario].definite ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/40 text-amber-300'}`}>{SCEN[i.scenario].label}</span></td>
                                  <td className="py-0.5 text-slate-500">{i.advantage || i.why}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {shown.length === 0 && <tr><td colSpan={9} className="px-2 py-2 text-slate-500">{plan.rows.length === 0 ? 'Nothing to clear.' : 'No customer in this scenario.'}</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {running && job && (
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <div className="w-40 h-2 rounded bg-slate-700 overflow-hidden"><div className="h-2 bg-blue-500 transition-all" style={{ width: `${job.total ? Math.round((100 * job.done) / job.total) : 0}%` }} /></div>
                <span className="font-mono">{job.done}/{job.total}</span>
                <button className="text-red-300 hover:underline" onClick={() => qboApi.cancelJob(job.jobId).catch(() => undefined)}>cancel</button>
              </div>
            )}
            <label className="ml-auto flex items-center gap-1 text-xs text-amber-300 cursor-pointer" title="Off: unmatched hand journals / deposits / checks stay on the customer for the bookkeeper. On: everything on the selected customers is cleared.">
              <input type="checkbox" checked={includeReview} onChange={(e) => setIncludeReview(e.target.checked)} disabled={running} /> include unconfirmed review items{!includeReview && excludedIds.length > 0 ? ` (${excludedIds.length} left on the customers)` : ''}
            </label>
            <label className="flex items-center gap-1 text-xs text-slate-400" title="Date of the MIGR journals and zero-dollar payments. The cutoff keeps an aging as of the cutoff clean; must be inside open books.">Post on
              <input type="date" value={postDate} onChange={(e) => setPostDate(e.target.value)} disabled={running} className="bg-slate-900 border border-slate-600 rounded px-1.5 py-0.5 text-xs text-slate-200" />
            </label>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${confirming ? 'bg-amber-600 hover:bg-amber-500' : 'bg-green-600 hover:bg-green-500'}`}
              onClick={run}
              disabled={running || selected.size === 0}
            >
              {running ? 'Clearing…' : confirming ? `Confirm: post ${selected.size} journal(s) + payment(s) dated ${postDate || 'today'}, move $${money(selectedClearNow)}` : `Post cleanup (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {plan && undoCandidates.length > 0 && (
        <div className="mt-4 bg-slate-900/60 rounded-lg p-3 border border-slate-700/60">
          <div className="text-xs font-semibold text-slate-400">Customers with an open MIGR journal ({undoCandidates.length}; {undoCandidates.filter((c) => Math.abs(c.remaining) < 0.005).length} exactly covered, {undoCandidates.filter((c) => Math.abs(c.remaining) >= 0.005).length} over- or under-covered)</div>
          <div className="text-[11px] text-slate-500 mt-0.5 mb-2">QuickBooks would not link their deposit / check lines to the zero-dollar payment, so the MIGR journal sits open next to the items. Exactly covered = nets to zero, nothing moves again; the bookkeeper applies the journal by hand (Receive payment, $0). Over-covered = a run was repeated: tick the customers, "What would undo delete", then delete — the newest MIGR journal goes each time; repeat until the remainder reads 0.00.</div>
          <div className="flex gap-2 mb-1 text-[11px]"><button className="text-blue-300 hover:underline" onClick={() => setUndoSel(new Set(undoCandidates.filter((c) => Math.abs(c.remaining) >= 0.005).map((c) => c.qboId)))}>select over-/under-covered</button><button className="text-blue-300 hover:underline" onClick={() => setUndoSel(new Set())}>clear</button></div>
          <div className="overflow-x-auto max-h-56 overflow-y-auto rounded border border-slate-700/60">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-slate-900"><tr>{['', 'Customer', 'Items', 'Open MIGR journal(s)', 'Remainder'].map((h, i) => <th key={i} className="text-left px-2 py-1 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">{h}</th>)}</tr></thead>
              <tbody>{undoCandidates.map((c) => (
                <tr key={c.qboId} className="hover:bg-slate-700/30">
                  <td className="px-2 py-0.5 border-b border-slate-800/60"><input type="checkbox" checked={undoSel.has(c.qboId)} onChange={() => setUndoSel((prev) => { const n = new Set(prev); if (n.has(c.qboId)) n.delete(c.qboId); else n.add(c.qboId); return n; })} disabled={undoing} /></td>
                  <td className="px-2 py-0.5 border-b border-slate-800/60 text-slate-300">{c.name} <span className="text-slate-500">QBO {c.qboId}</span></td>
                  <td className="px-2 py-0.5 border-b border-slate-800/60 font-mono text-right">{c.itemCount} · {money(c.items)}</td>
                  <td className="px-2 py-0.5 border-b border-slate-800/60 font-mono text-right">{money(c.journalOpen)}</td>
                  <td className={`px-2 py-0.5 border-b border-slate-800/60 font-mono text-right ${Math.abs(c.remaining) >= 0.005 ? 'text-amber-300' : 'text-green-400'}`}>{money(c.remaining)}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 disabled:opacity-50" onClick={undoPlan} disabled={undoSel.size === 0 || undoing}>What would undo delete ({undoSel.size})</button>
            {undoRows && undoRows.length > 0 && (
              <>
                <span className="text-[11px] text-slate-400">{undoRows.filter((x) => !x.error).map((x) => `${x.docNumber} ${x.date} $${money(x.amount)}${x.paymentId ? ' + payment' : ''}${x.previousDocNumber ? ` → back to ${x.previousDocNumber}` : ''}`).join(' · ')}{undoRows.some((x) => x.error) ? ` · skipped: ${undoRows.filter((x) => x.error).map((x) => `${x.qboId} (${x.error})`).join(', ')}` : ''}</span>
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50" onClick={undoRun} disabled={undoing || undoRows.every((x) => x.error)}>{undoing ? 'Deleting…' : `Delete ${undoRows.filter((x) => !x.error).length} journal(s)`}</button>
              </>
            )}
          </div>
        </div>
      )}

      <SyncResultList result={result} title="Cleanup run" />
    </div>
  );
}
